import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { GiftIcon, MapIcon, HeadphonesIcon, HomeIcon, CheckIcon } from 'lucide-react';
export function BonusesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const bonuses = [{
    number: '#1',
    icon: MapIcon,
    title: 'El Mapa del Linaje',
    subtitle: 'Guía Práctica de Genograma de Fertilidad',
    value: '$17 USD',
    hook: '¿Y si el bloqueo no es tuyo... sino de una historia que todavía no conocés?',
    description: 'Las lealtades invisibles no se ven en una ecografía. Se ven en un árbol. Con esta guía vas a aprender a leer tu árbol genealógico con ojos sistémicos y encontrar el nudo exacto que detiene el flujo de vida.',
    bullets: [{
      bold: 'Identificá los "Nudos Silenciosos":',
      text: ' Abortos no nombrados, duelos no llorados, miedos heredados de tus abuelas que tu cuerpo siente como propios.'
    }, {
      bold: 'Las 15 Preguntas Clave:',
      text: ' Exactamente qué preguntarle a tu madre, tías o abuelas para desenterrar la raíz invisible del bloqueo.'
    }, {
      bold: 'Simbología Sistémica:',
      text: ' Aprendé a marcar en tu mapa el lugar exacto donde la energía de fertilidad se interrumpió para poder restaurarla con el Método R.E.A.'
    }],
    quote: '"Cuando tomé conciencia que el niño de mis ancestros había sido robado y que eso causó un dolor muy grande, entendí y le expliqué a mi conciencia que eso no iba a volver a ocurrir."'
  }, {
    number: '#2',
    icon: HeadphonesIcon,
    title: 'Audio-Meditación "El Abrazo de la Abuela"',
    subtitle: 'Reordenamiento Sistémico con Base Bíblica',
    value: '$29 USD',
    hook: 'Sanar el árbol no es un proceso intelectual. Es un proceso del alma.',
    description: 'Este audio de 12 minutos es el puente entre tu mente consciente y el sistema familiar que vive en tus células. Fue diseñado con frecuencias Alpha, visualización guiada y la autoridad de la Palabra de Dios para que tu cuerpo deje de "luchar" y empiece a "recibir".',
    bullets: [{
      bold: 'Frecuencias Alpha de Calma:',
      text: ' Diseñadas para bajar el cortisol (la hormona del estrés que bloquea la ovulación) y preparar tu biología para la receptividad.'
    }, {
      bold: 'El Reordenamiento:',
      text: ' Una visualización poderosa respaldada por Salmo 139, Hebreos 11:11 y Éxodo 23:26 donde te situás frente a tus ancestras, devolvés las cargas que no te pertenecen y recibís, finalmente, su bendición para ser madre.'
    }, {
      bold: 'Uso Nocturno:',
      text: ' Ideal para las 11 PM, el momento más oscuro del ciclo. Transforma la ansiedad del test negativo en un espacio de paz, fe y permiso biológico.'
    }],
    quote: '"Porque por la fe Sara también recibió fuerza para concebir." — Hebreos 11:11'
  }, {
    number: '#3',
    icon: HomeIcon,
    title: 'La Cuna Emocional',
    subtitle: 'Protocolo de 7 Días para Preparar tu Hogar',
    value: '$15 USD',
    hook: 'El milagro necesita un lugar donde aterrizar.',
    description: 'Podés tener el árbol sanado y la fe encendida, pero si tu hogar sigue cargado de pasado, el alma del bebé no encuentra su lugar. Este protocolo práctico te guía día a día para preparar tu entorno físico y psíquico.',
    bullets: [{
      bold: 'El Vacío Fértil:',
      text: ' Cómo limpiar tu casa del "ruido sistémico" y los objetos del pasado que ocupan inconscientemente el lugar del bebé.'
    }, {
      bold: 'El Lugar del Padre:',
      text: ' El ajuste exacto en tu habitación matrimonial para que la energía de protección del padre libere a la madre para gestar.'
    }, {
      bold: 'Anclaje de Bienvenida:',
      text: ' Un ejercicio diario de 5 minutos para que tu mente, tu cuerpo y tu hogar declaren juntos: "Hay un lugar listo para vos."'
    }],
    quote: null
  }];
  return <section ref={ref} className="w-full bg-gradient-to-b from-cream to-white py-6 md:py-10 px-6" data-id="element-16">
      <div className="max-w-4xl mx-auto" data-id="element-17">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} data-id="element-18">
          <div className="text-center mb-8" data-id="element-19">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-brown mb-4" data-id="element-20">
              Pero no quiero que hagas este viaje sola...
            </h2>
            <p className="font-sans text-base md:text-lg text-brown/80 leading-relaxed max-w-2xl mx-auto mb-3" data-id="element-21">
              Sé que el camino de la fertilidad puede volverse agotador,
              solitario y lleno de preguntas sin respuesta. Por eso no te
              entrego solo un libro.
            </p>
            <p className="font-serif text-xl md:text-2xl text-rose font-semibold mb-3" data-id="element-22">
              Te entrego un protocolo completo de sanación.
            </p>
            <p className="font-sans text-base text-brown/80 leading-relaxed max-w-2xl mx-auto" data-id="element-23">
              Porque la teoría sin herramientas no transforma. Y vos merecés
              pasar del "entiendo el concepto" al "siento el cambio en mi
              cuerpo" desde el primer día.
            </p>
            <p className="font-sans text-base md:text-lg text-brown font-medium mt-4" data-id="element-24">
              Al ordenar hoy "El Código del Vientre", recibís de regalo estos 3
              tesoros:
            </p>
          </div>

          <div className="space-y-6" data-id="element-25">
            {bonuses.map((bonus, index) => {
            const Icon = bonus.icon;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.6,
              delay: index * 0.15
            }} className="bg-white rounded-3xl p-6 md:p-8 shadow-md border border-gold/20" data-id="element-26">
                  <div className="flex items-center gap-3 mb-4" data-id="element-27">
                    <GiftIcon className="w-7 h-7 text-rose" data-id="element-28" />
                    <span className="font-serif text-xl font-bold text-rose" data-id="element-29">
                      REGALO {bonus.number}
                    </span>
                  </div>

                  <div className="flex items-start gap-4 mb-4" data-id="element-30">
                    <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center" data-id="element-31">
                      <Icon className="w-7 h-7 text-gold" strokeWidth={1.5} data-id="element-32" />
                    </div>
                    <div data-id="element-33">
                      <h3 className="font-serif text-xl md:text-2xl font-bold text-brown mb-1" data-id="element-34">
                        {bonus.title}
                      </h3>
                      <p className="font-sans text-sm text-brown/70 italic" data-id="element-35">
                        {bonus.subtitle}
                      </p>
                    </div>
                  </div>

                  <div className="bg-cream/50 rounded-xl p-3 mb-4 text-center" data-id="element-36">
                    <span className="font-sans text-sm text-brown/60" data-id="element-37">
                      Valor real:{' '}
                    </span>
                    <span className="font-serif text-lg text-brown/40 line-through" data-id="element-38">
                      {bonus.value}
                    </span>
                    <span className="font-sans text-sm text-brown/60" data-id="element-39">
                      {' '}
                      — HOY:{' '}
                    </span>
                    <span className="font-serif text-lg font-bold text-rose" data-id="element-40">
                      GRATIS
                    </span>
                  </div>

                  <p className="font-sans text-base text-brown/90 leading-relaxed font-semibold italic mb-3" data-id="element-41">
                    {bonus.hook}
                  </p>
                  <p className="font-sans text-base text-brown/80 leading-relaxed mb-4" data-id="element-42">
                    {bonus.description}
                  </p>

                  <div className="space-y-2 mb-4" data-id="element-43">
                    {bonus.bullets.map((bullet, i) => <div key={i} className="flex gap-2 items-start" data-id="element-44">
                        <CheckIcon className="w-5 h-5 text-rose flex-shrink-0 mt-0.5" data-id="element-45" />
                        <p className="font-sans text-sm md:text-base text-brown/80 leading-relaxed" data-id="element-46">
                          <span className="font-semibold" data-id="element-47">{bullet.bold}</span>
                          {bullet.text}
                        </p>
                      </div>)}
                  </div>

                  {bonus.quote && <blockquote className="border-l-4 border-gold pl-4 py-1 italic" data-id="element-48">
                      <p className="font-serif text-sm md:text-base text-brown/70" data-id="element-49">
                        {bonus.quote}
                      </p>
                    </blockquote>}
                </motion.div>;
          })}
          </div>

          {/* Value Stack Table */}
          <div className="mt-10 bg-gradient-to-br from-rose/10 to-gold/10 rounded-3xl p-6 md:p-8 border-2 border-gold/30" data-id="element-50">
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-brown text-center mb-2" data-id="element-51">
              📦 Lo Que Recibís Hoy
            </h3>
            <p className="font-sans text-sm text-brown/60 text-center mb-6" data-id="element-52">
              Acceso completo · Descarga inmediata
            </p>

            <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-id="element-53">
              {[{
              item: '📖 Libro "El Código del Vientre"',
              value: '$27',
              today: 'Incluido'
            }, {
              item: '🗺️ Bono 1: El Mapa del Linaje',
              value: '$17',
              today: 'GRATIS'
            }, {
              item: '🎧 Bono 2: El Abrazo de la Abuela',
              value: '$29',
              today: 'GRATIS'
            }, {
              item: '🏠 Bono 3: La Cuna Emocional',
              value: '$15',
              today: 'GRATIS'
            }].map((row, i) => <div key={i} className={`flex items-center justify-between gap-3 p-4 ${i !== 0 ? 'border-t border-cream' : ''}`} data-id="element-54">
                  <span className="font-sans text-sm md:text-base text-brown/90 flex-1" data-id="element-55">
                    {row.item}
                  </span>
                  <div className="flex items-center gap-3 flex-shrink-0" data-id="element-56">
                    <span className="font-sans text-sm text-brown/40 line-through" data-id="element-57">
                      {row.value}
                    </span>
                    <span className={`font-sans text-xs md:text-sm font-bold px-3 py-1 rounded-full ${row.today === 'GRATIS' ? 'bg-rose/10 text-rose' : 'bg-gold/10 text-gold'}`} data-id="element-58">
                      ✓ {row.today}
                    </span>
                  </div>
                </div>)}
            </div>

            <div className="mt-5 bg-white rounded-2xl p-5 shadow-sm border border-gold/30" data-id="element-59">
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-cream" data-id="element-60">
                <span className="font-sans text-sm md:text-base text-brown/70" data-id="element-61">
                  Valor total
                </span>
                <span className="font-serif text-xl md:text-2xl text-brown/40 line-through" data-id="element-62">
                  $88 USD
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-63">
                <div data-id="element-64">
                  <p className="font-sans text-sm text-rose font-semibold" data-id="element-65">
                    🔥 Precio hoy
                  </p>
                  <p className="font-sans text-xs text-brown/60" data-id="element-66">Pago único</p>
                </div>
                <span className="font-serif text-3xl md:text-4xl font-bold text-rose whitespace-nowrap" data-id="element-67">
                  $14.00 USD
                </span>
              </div>
            </div>

            <div className="mt-5 bg-gold/10 rounded-xl p-4 border border-gold/30" data-id="element-68">
              <p className="font-sans text-sm md:text-base text-brown/90 leading-relaxed text-center" data-id="element-69">
                <span className="font-semibold" data-id="element-70">⏳ IMPORTANTE:</span> Este
                precio de lanzamiento es temporal. El protocolo completo volverá
                a su precio normal de{' '}
                <span className="font-semibold" data-id="element-71">$88 USD</span> cuando cerremos
                esta ventana de acceso especial.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}